import Model from '@/store/plugins/orm/Model'
import { CashRegisterTransaction, Shift, Order } from './ModelRepository'

import CashRegisterService from '@/services/CashRegisterService';
import EinvoiceService from '@/services/EinvoiceService';
import FiscalServiceLegacy from '@/services/FiscalServiceLegacy';
import i18n from '@/i18n';
import Store from '@/store';

export class CashRegister extends Model {
    static get entity() {
        return 'cash_registers';
    }

    static fields() {
        return {
            id: this.attr(null),
            hash: this.attr(null),
            activation_code: this.attr(null),
            name: this.attr(null),
            uuid: this.attr(null),
            platform: this.attr(null),
            manufacturer: this.attr(null),
            model: this.attr(null),
            status: this.attr(null),
            settings: this.attr(null),
            created_at: this.attr(null),
            shared: this.attr(false),

            transactions: this.hasMany(CashRegisterTransaction, 'cash_register_id')
        };
    }

    static active() {
        return CashRegister.all().filter(cashRegister => cashRegister.status === 'active');
    }

    getCreatedAt() {
        if(!this.created_at){
            return null;
        }

        return moment.utc(this.created_at).local();
    }

    relevantTransactions() {
        if(locale !== 'me') {
            return this.transactions
        }

        const curDay = moment().tz('Europe/Podgorica').format('YYYY-MM-DD');
        return this.transactions.filter(transaction => {
            const issedAt = moment.utc(transaction.issued_at).tz('Europe/Podgorica')
            return issedAt.format('YYYY-MM-DD') === curDay;
        })
    }

    relevantOrders() {
        const curShift = Shift.current();
        const curShiftOrders = curShift ? curShift.getOrders() : [];

        if(locale !== 'me') {
            return curShiftOrders
        }

        const curDay = moment().tz('Europe/Podgorica').format('YYYY-MM-DD');
        return Order.all().filter(order => {
            const lastActivityAt = moment.utc(order.last_activity_at).tz('Europe/Podgorica')
            return lastActivityAt.format('YYYY-MM-DD') === curDay && order.cash_register_id == this.id;
        })
    }

    getCash() {
        return this.relevantTransactions().reduce((acc, cur) => acc.add(cur.getAmount()), Money.parse(0))
            .add(this.getPaidCash())
    }

    getPaidCash() {
        return this.relevantOrders()
            .filter(order => order.isCashExchanged())
            .reduce((acc, cur) => acc.add(cur.getPaidCash()), Money.parse(0));
    }

    depositRequired() {
        if(locale !== 'me') {
            return false
        }

        const tenant = this.$store().getters.tenant
        const noncashOnly = tenant && tenant.venue && !!tenant.venue.settings.noncash_only

        if(noncashOnly) {
            return false
        }

        const curDay = moment().tz('Europe/Podgorica').format('YYYY-MM-DD');
        const depositAdded = this.relevantTransactions().some(transaction => {
            if(transaction.amount < 0) {
                return false
            }

            const issuedAt = moment.utc(transaction.issued_at).tz('Europe/Podgorica')
            return issuedAt.format('YYYY-MM-DD') === curDay
        })

        return !depositAdded
    }

    async addTransaction(value) {
        if(!this.$store().getters.tenant) {
            return { success: false };
        }

        let curShift = Shift.current();
        if(!curShift) return { success: false };

        const limit = locale === 'sr' ? 999999 : 9999;
        if(value > limit) return { success: false };

        if(locale === 'me' && value > 0 && !this.depositRequired()) return { success: false };
        if(locale !== 'me' && value == 0) return { success: false };

        if(locale === 'ba') {
            const result = value > 0
                ? await FiscalServiceLegacy.depositMoney(value)
                : await FiscalServiceLegacy.withdrawMoney(value)

            if(!result.success) return { success: false }
        }

        let transaction = new CashRegisterTransaction({
            cash_register_id: this.id,
            shift_id: curShift.id,
            shift_hash: curShift.hash,
            amount: value,
            issued_at: moment().utc().format(CashRegisterTransaction.DATE_FORMAT),
            fiscal_code: null,
            register_status: null,
            registered_at: null,
            sync_needed: this.$store().getters.tenant.venue.hash,
        })

        return transaction.sync();
    }

    static availablePaymentTypes() {
        const noncashOnly = !!this.store().getters.tenant?.venue?.settings.noncash_only;
        const einvoiceOnly = EinvoiceService.einvoiceOnly();

        return [
            { value: 'cash', enabled: !noncashOnly && !einvoiceOnly },
            { value: 'credit_card', enabled: !noncashOnly && !einvoiceOnly  },
            { value: 'invoice', enabled: true },
            { value: 'credit_card_business', enabled: locale === 'me' },
            { value: 'advance', enabled: true },
            { value: 'check', enabled: locale !== 'me' && !einvoiceOnly  },
            { value: 'voucher', enabled: locale !== 'me' && locale !== 'ba' && !einvoiceOnly  },
            { value: 'mobile_money', enabled: locale !== 'me' && locale !== 'ba' && !einvoiceOnly  },
            { value: 'other_noncash', enabled: locale !== 'ba' && !einvoiceOnly },
        ]
        .filter(type => type.enabled)
        .map(type => {
            return {
                value: type.value,
                display: i18n.t(`orders.payment_types.${type.value}`),
            }
        })
    }

    allowedPaymentTypes() {
        const availablePaymentTypes = CashRegister.availablePaymentTypes();
        return availablePaymentTypes.filter(type => !!this.settings.payments[type.value]);
    }

    allowsPayment(paymentType) {
        if (!Array.isArray(paymentType)) {
            paymentType = [paymentType];
        }

        return paymentType.every(p => this.allowedPaymentTypes().some(type => type.value === p))
    }

    allowsOrderPayment(type, value) {
        let allowed = false;
        if(type === 'mixed_payment' || type === 'advance') {
            const payments = value.map(payment => payment.type);
            allowed = this.allowsPayment(payments);
        } else if (['credit_card', 'other_noncash'].includes(type) && value) {
            allowed = this.allowsPayment([type, 'cash']);
        } else {
            allowed = this.allowsPayment(type);
        }

        return allowed;
    }

    getOpenShift() {
        return Shift.all().find(shift => !shift.closed_at && shift.cash_register_id === this.id)
    }

    getFullName() {
        if(locale === 'ba') return (this.name ? (this.name + ' / ') : '') + this.activation_code

        return (this.name ? (this.name + ' / ') : '') + this.hash
    }

    /**
     * Get the cash register active on this device
     */
    static current() {
        return CashRegister.query()
            .with('transactions')
            .find(CashRegisterService.getId())
    }
}

export default CashRegister
